import { useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { getUserDataFromLocalStorage } from "@/utils";
import { PostTokenExpiryWarningMsg } from "@/services/PostMessageService";

type JWTPayload = { exp: number };

const WARNING_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Monitors the current JWT token and sends a PostMessage to the parent
 * 5 minutes before expiry. Checks every 30 seconds via setInterval and
 * re-checks immediately when the tab becomes visible again.
 */

const useTokenExpiryWarning = (token: string | null | undefined) => {
  const hasFiredRef = useRef(false);

  useEffect(() => {
    // Reset when token changes (e.g. re-login with a new token)
    hasFiredRef.current = false;

    const check = () => {
      if (hasFiredRef.current) return;

      const resolvedToken = token ?? getUserDataFromLocalStorage()?.token;
      if (!resolvedToken) return;

      let expMs: number;
      try {
        const { exp } = jwtDecode<JWTPayload>(resolvedToken);
        if (!exp) return;
        expMs = exp * 1000;
      } catch {
        return;
      }

      const now = Date.now();
      const timeUntilWarning = expMs - WARNING_THRESHOLD_MS - now;
      // Good to have the minutes left for debugging - uncomment if needed
      // const timeUntilExpiry = expMs - now;
      // const minutesLeft = Math.max(0, timeUntilExpiry / 1000 / 60).toFixed(1);

      if (timeUntilWarning <= 0 && now < expMs) {
        hasFiredRef.current = true;
        window.parent.postMessage(PostTokenExpiryWarningMsg(), "*");
      }
    };

    // Check immediately on mount / token change
    check();

    // Periodically re-check every 60 seconds
    const interval = setInterval(check, 60 * 1000);

    // Re-check instantly when the tab becomes visible again (handles throttled intervals)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token]);
};

export default useTokenExpiryWarning;
